/**
 * Service FCM côté client.
 * Gère :
 *  - vérification du support navigateur
 *  - état de la permission
 *  - enregistrement du Service Worker firebase-messaging-sw.js
 *  - récupération du token FCM via VAPID
 *  - envoi du token au backend
 *  - listener foreground (onMessage) pour afficher un toast
 */
import axios from 'axios';
import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { getFirebaseMessaging, VAPID_KEY, firebaseConfig } from '../firebase/config';
import { tokenService } from './tokenService';
import logger from './logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/push`;

const authHeaders = () => {
  const t = tokenService.getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

let foregroundUnsubscribe = null;
let cachedRegistration = null;

async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  if (cachedRegistration) return cachedRegistration;
  cachedRegistration = await navigator.serviceWorker.register(
    '/firebase-messaging-sw.js',
    { scope: '/firebase-cloud-messaging-push-scope' }
  );
  await navigator.serviceWorker.ready;
  return cachedRegistration;
}

export const fcmService = {
  isConfigured() {
    return Boolean(firebaseConfig.apiKey && VAPID_KEY);
  },

  isSupported() {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  },

  getPermissionState() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission; // 'granted' | 'denied' | 'default'
  },

  /**
   * Demande la permission, récupère le token, l'envoie au backend.
   * Retourne { token, permission } ou null si échec/refus.
   */
  async enable() {
    if (!this.isSupported()) {
      return { error: 'unsupported' };
    }
    if (!this.isConfigured()) {
      return { error: 'not_configured' };
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return { error: 'permission_denied', permission };
      }

      const messaging = await getFirebaseMessaging();
      if (!messaging) return { error: 'unsupported' };

      const registration = await ensureServiceWorker();

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        return { error: 'no_token' };
      }

      // Envoyer au backend
      await axios.post(
        `${API}/fcm-token`,
        {
          token,
          user_agent: navigator.userAgent,
          platform: navigator.platform || 'web',
        },
        { headers: authHeaders() }
      );

      // Listener foreground (onMessage) : déclencher un event que l'app peut écouter
      this.setupForegroundListener(messaging);

      return { token, permission };
    } catch (err) {
      logger.error('[FCM] enable error:', err);
      return { error: 'exception', message: err?.message };
    }
  },

  /**
   * Écoute les messages reçus quand l'onglet est actif.
   * Le SW ne déclenche PAS showNotification dans ce cas — on laisse le client gérer.
   */
  setupForegroundListener(messaging) {
    if (foregroundUnsubscribe) return; // déjà initialisé
    foregroundUnsubscribe = onMessage(messaging, (payload) => {
      const notif = payload.notification || {};
      const data = payload.data || {};
      window.dispatchEvent(
        new CustomEvent('fcm-message', {
          detail: {
            title: notif.title || data.title || 'GreenLink Agritech',
            body: notif.body || data.body || '',
            url: data.url || data.click_action || '/',
            data,
          },
        })
      );
    });
  },

  /**
   * Désabonne (supprime le token côté client + côté serveur).
   */
  async disable() {
    try {
      const messaging = await getFirebaseMessaging();
      if (messaging) {
        try {
          await deleteToken(messaging);
        } catch (e) {
          logger.debug('[FCM] deleteToken skipped (already deleted?):', e?.message);
        }
      }
      await axios.delete(`${API}/fcm-token`, { headers: authHeaders() });
      if (foregroundUnsubscribe) {
        foregroundUnsubscribe();
        foregroundUnsubscribe = null;
      }
      return { ok: true };
    } catch (err) {
      logger.error('[FCM] disable error:', err);
      return { ok: false, error: err?.message };
    }
  },

  /**
   * Démarre uniquement le listener foreground si la permission est déjà accordée
   * (utile pour les utilisateurs qui ont déjà activé les notifications avant).
   */
  async restoreIfGranted() {
    if (!this.isSupported() || !this.isConfigured()) return;
    if (Notification.permission !== 'granted') return;
    try {
      const messaging = await getFirebaseMessaging();
      if (!messaging) return;
      await ensureServiceWorker();
      this.setupForegroundListener(messaging);
    } catch (err) {
      logger.debug('[FCM] restoreIfGranted skipped:', err?.message);
    }
  },
};

export default fcmService;
